import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '../../environments/environment';

/** Who currently has access to one bot, plus who created it. Populated by
 *  GET /access/bot/by-bot/{bot_id} so the access panel can render every client
 *  user with a toggle in a single request. */
export interface BotAccessState {
  bot_id: string;
  created_by_user_id: string | null;
  /** User ids holding any grant (view / edit / owner). */
  user_ids: string[];
  /** Subset of user_ids whose grant is 'owner' — badged and not revocable. */
  owner_user_ids: string[];
}

/** Who has access to one knowledge base. Buckets carry a single owner_user_id
 *  (not an ACL 'owner' level like bots), so it is a scalar here. */
export interface KbAccessState {
  bucket_id: string;
  owner_user_id: string | null;
  created_by_user_id: string | null;
  user_ids: string[];
}

@Injectable({ providedIn: 'root' })
export class AccessService {
  private readonly tutorsBase = environment.tutorsBackend;
  private readonly filesBase = environment.filesBackend;

  constructor(private http: HttpClient) {}

  // ─── Bots ────────────────────────────────────────────────────────

  getBotAccess(botId: string): Promise<BotAccessState> {
    return firstValueFrom(
      this.http
        .get<{ data: BotAccessState }>(`${this.tutorsBase}/access/bot/by-bot/${botId}`)
        .pipe(map((r) => r.data)),
    );
  }

  /** Bot ids a user holds an access row for (owned + granted). Backs the
   *  "filter by user" dropdown on the agents overview — the inverse lookup of
   *  getBotAccess. Owner rows written at creation time mean this includes the
   *  user's own agents. */
  getUserBotIds(userId: string): Promise<string[]> {
    return firstValueFrom(
      this.http
        .get<{ data: { bot_id: string }[] }>(`${this.tutorsBase}/access/bot/${userId}`)
        .pipe(map((r) => (r.data || []).map((d) => d.bot_id))),
    );
  }

  grantBot(userId: string, botId: string): Promise<void> {
    return firstValueFrom(
      this.http
        .post(`${this.tutorsBase}/access/bot`, { user_id: userId, bot_id: botId })
        .pipe(map(() => undefined)),
    );
  }

  revokeBot(userId: string, botId: string): Promise<void> {
    return firstValueFrom(
      this.http
        .delete(`${this.tutorsBase}/access/bot/${userId}/${botId}`)
        .pipe(map(() => undefined)),
    );
  }

  // ─── Knowledge bases ─────────────────────────────────────────────

  getBucketAccess(bucketId: string): Promise<KbAccessState> {
    return firstValueFrom(
      this.http
        .get<{ data: KbAccessState }>(
          `${this.filesBase}/access/knowledge-base/by-bucket/${bucketId}`,
        )
        .pipe(map((r) => r.data)),
    );
  }

  /** Bucket ids a user may see (owned + granted). Backs the "filter by user"
   *  dropdown on the knowledge-base page. */
  getUserBucketIds(userId: string): Promise<string[]> {
    return firstValueFrom(
      this.http
        .get<{ data: { bucket_ids: string[] } }>(
          `${this.filesBase}/access/knowledge-base/visible/${userId}`,
        )
        .pipe(map((r) => r.data?.bucket_ids || [])),
    );
  }

  grantKb(userId: string, bucketId: string): Promise<void> {
    return firstValueFrom(
      this.http
        .post(`${this.filesBase}/access/knowledge-base`, {
          user_id: userId,
          bucket_id: bucketId,
        })
        .pipe(map(() => undefined)),
    );
  }

  revokeKb(userId: string, bucketId: string): Promise<void> {
    return firstValueFrom(
      this.http
        .delete(`${this.filesBase}/access/knowledge-base/${userId}/${bucketId}`)
        .pipe(map(() => undefined)),
    );
  }
}
